/**
 * Release queue (SPEC_Hazard_UI §3.1) — a worklist, not a dashboard. No KPIs.
 *
 * Bulk confirmation stops at siBase 55 and is refused outright for anything
 * person-related. The control is disabled *with its reason on screen* rather
 * than silently: a greyed-out button with no explanation gets read as a bug and
 * then worked around.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import { useModalContext } from "../../components/ModalProvider";
import { useLegalAdministrationAPI } from "../../LegalAdministrationAPI";
import { SignoffPill, damageTypeLabel, formatDate } from "./hazardDisplay";
import HazardReviewPanel from "./HazardReviewPanel";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Properties {
  refreshSignal: number;
  reviewCode?: string | null;
  onReviewCode: (code: string | null) => void;
  onChanged: () => void;
}

/** UI §3.3 — above this, single sign-off only. */
const BULK_CONFIRM_MAX_SI_BASE = 55;

const GROUP_ORDER = ["firstReview", "changesToReview", "fullReviewDue", "severityUnconfirmed"] as const;

export default function HazardReleasesPanel(properties: Properties) {
  const { t } = useTranslation();
  const { pushError, pushSuccess } = useModalContext() as any;
  const legalAPI = useLegalAdministrationAPI();

  const [queue, setQueue] = useState<any>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const load = async () => {
    try {
      const result = await legalAPI.getHazardReviewQueue();
      setQueue(result);
      setSelected({});
    } catch (error) {
      pushError(error);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties.refreshSignal]);

  const severityRows: any[] = queue?.groups?.severityUnconfirmed || [];

  const selectedSeverities = useMemo(
    () =>
      severityRows.filter(
        (row: any) => selected[`${row.regulatedObjectCode}:${row.hazardCode}`],
      ),
    [severityRows, selected],
  );

  /** Mirrors the server's rule so the reason can be shown before submitting. */
  const bulkBlocker = useMemo(() => {
    if (selectedSeverities.length < 2) return null;
    if (selectedSeverities.some((row: any) => row.damageType === "person")) return "containsPerson";
    if (selectedSeverities.some((row: any) => (row.siBase ?? 0) > BULK_CONFIRM_MAX_SI_BASE)) {
      return "aboveThreshold";
    }
    return null;
  }, [selectedSeverities]);

  const confirmSelected = async () => {
    try {
      setSaving(true);
      const count = selectedSeverities.length;
      await legalAPI.confirmHazardSeverities(
        selectedSeverities.map((row: any) => ({
          regulatedObjectCode: row.regulatedObjectCode,
          hazardCode: row.hazardCode,
        })),
      );
      await load();
      properties.onChanged();
      pushSuccess(t("hazard.releases.confirmedSuccess", { count }));
    } catch (error) {
      pushError(error);
    } finally {
      setSaving(false);
    }
  };

  if (properties.reviewCode) {
    return (
      <HazardReviewPanel
        regulatedObjectCode={properties.reviewCode}
        onClose={(didChange: boolean) => {
          properties.onReviewCode(null);
          if (didChange) {
            load();
            properties.onChanged();
          }
        }}
      />
    );
  }

  if (!queue) {
    return (
      <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-3">
        <Skeleton className="h-6 w-[260px]" />
        <Skeleton className="h-[200px] w-full" />
        <Skeleton className="h-6 w-[260px]" />
        <Skeleton className="h-[140px] w-full" />
      </div>
    );
  }

  const totalOpen = GROUP_ORDER.reduce((sum, key) => sum + (queue.totals?.[key] || 0), 0);

  if (totalOpen === 0) {
    // An empty state that says what happens next, not just that nothing is here.
    return (
      <div className="flex flex-col items-center gap-2 py-6 text-center">
        <p className="text-sm font-semibold">{t("hazard.releases.emptyNothingOpen")}</p>
        <p className="max-w-prose text-sm text-muted-foreground">
          {t("hazard.releases.emptyNothingOpenHint")}
        </p>
      </div>
    );
  }

  const allSeveritiesSelected =
    severityRows.length > 0 && selectedSeverities.length === severityRows.length;

  const toggleAllSeverities = (checked: boolean) => {
    setSelected((previous) => {
      const next = { ...previous };
      severityRows.forEach((row: any) => {
        next[`${row.regulatedObjectCode}:${row.hazardCode}`] = checked;
      });
      return next;
    });
  };

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-6">
      {GROUP_ORDER.map((groupKey) => {
        const rows: any[] = queue.groups?.[groupKey] || [];
        if (rows.length === 0) return null;

        const isSeverityGroup = groupKey === "severityUnconfirmed";

        return (
          <section key={groupKey} className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 flex-wrap items-baseline gap-3">
              <h3 className="font-semibold">
                {t(`hazard.releases.group.${groupKey}`)} ({rows.length})
              </h3>
              <p className="min-w-0 break-words text-xs text-muted-foreground">
                {t(`hazard.releases.groupHint.${groupKey}`)}
              </p>
            </div>

            <div className="min-w-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {isSeverityGroup ? (
                      <TableHead className="w-[44px]">
                        <Checkbox
                          checked={allSeveritiesSelected}
                          aria-label={t("hazard.releases.selectAll")}
                          onCheckedChange={(checked) => toggleAllSeverities(checked === true)}
                        />
                      </TableHead>
                    ) : null}
                    <TableHead className="w-[230px]">{t("hazard.types.type")}</TableHead>
                    <TableHead>{t("hazard.releases.trigger")}</TableHead>
                    <TableHead className="w-[150px]">{t("hazard.releases.damageTypes")}</TableHead>
                    <TableHead className="w-[90px]">{t("hazard.releases.severity")}</TableHead>
                    <TableHead className="w-[90px]">{t("hazard.releases.age")}</TableHead>
                    <TableHead className="w-[170px]">
                      <span className="sr-only">{t("hazard.releases.openReview")}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row: any) => {
                    const key = isSeverityGroup
                      ? `${row.regulatedObjectCode}:${row.hazardCode}`
                      : row.regulatedObjectCode;
                    return (
                      <TableRow key={key}>
                        {isSeverityGroup ? (
                          <TableCell>
                            <Checkbox
                              checked={!!selected[key]}
                              aria-label={t("hazard.releases.selectRow", {
                                type: row.name,
                                hazard: row.hazardLabel,
                              })}
                              onCheckedChange={(checked) =>
                                setSelected((previous) => ({ ...previous, [key]: checked === true }))
                              }
                            />
                          </TableCell>
                        ) : null}
                        <TableCell>
                          <div className="font-semibold">{row.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {isSeverityGroup ? row.hazardLabel : row.regulatedObjectCode}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {t(`hazard.trigger.${row.trigger}`, { defaultValue: row.trigger })}
                          {row.deltasSinceFullReview > 0 ? (
                            <span className="text-muted-foreground">
                              {" "}
                              · {t("hazard.releases.deltas", { count: row.deltasSinceFullReview })}
                            </span>
                          ) : null}
                          {row.lastFullReviewAt ? (
                            <span className="text-muted-foreground">
                              {" "}
                              · {t("hazard.types.lastFullReview")}: {formatDate(row.lastFullReviewAt)}
                            </span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-sm">
                          {isSeverityGroup
                            ? damageTypeLabel(t, row.damageType)
                            : (row.damageTypes || [])
                                .map((damageType: string) => damageTypeLabel(t, damageType))
                                .join(", ") || "—"}
                        </TableCell>
                        <TableCell className="text-sm tabular-nums">
                          {row.highestSiBase ?? row.siBase ?? (
                            <span
                              className="text-muted-foreground"
                              title={t("hazard.severity.notAssessableHint")}
                            >
                              {t("hazard.severity.notAssessableShort")}
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {row.ageDays !== null && row.ageDays !== undefined
                            ? t("hazard.releases.ageDays", { days: row.ageDays })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          {!isSeverityGroup ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <SignoffPill state={row.signoffState} t={t} />
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => properties.onReviewCode(row.regulatedObjectCode)}
                              >
                                {t("hazard.releases.openReview")}
                              </Button>
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {isSeverityGroup ? (
              <div className="flex min-w-0 flex-wrap items-center gap-3">
                <Button
                  className="gap-2 max-sm:w-full"
                  disabled={saving || selectedSeverities.length === 0 || !!bulkBlocker}
                  onClick={() => setConfirmDialogOpen(true)}
                >
                  {saving ? <Loader2 aria-hidden className="h-4 w-4 animate-spin" /> : null}
                  {t("hazard.releases.confirmSelected", { count: selectedSeverities.length })}
                </Button>
                {bulkBlocker ? (
                  <p className="min-w-0 break-words text-sm text-warning">
                    {bulkBlocker === "containsPerson"
                      ? t("hazard.releases.bulkBlockedPerson")
                      : t("hazard.releases.bulkBlockedThreshold", { max: BULK_CONFIRM_MAX_SI_BASE })}
                  </p>
                ) : null}
              </div>
            ) : null}
          </section>
        );
      })}

      {/* Bestätigte Schweren werden verbindlich und protokolliert — Ausweg vor
          dem Schreiben. */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="w-[calc(100vw-2rem)] sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hazard.releases.confirmDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              {t("hazard.releases.confirmDialogMessage", { count: selectedSeverities.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="max-sm:w-full">{t("global.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              className="max-sm:w-full"
              onClick={() => {
                setConfirmDialogOpen(false);
                confirmSelected();
              }}
            >
              {t("hazard.releases.confirmSelected", { count: selectedSeverities.length })}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
